import { useCallback, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useGlobalAudioClock, useGlobalAudioPlayback } from './GlobalAudioContext';

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/** Scrub/seek against the shell audio element. Subscribes to clock — mount only for the active row/bar. */
export function useGlobalAudioScrub() {
  const { currentTimeSec, durationSec, progressRatio } = useGlobalAudioClock();
  const { seek, beginSeekSilence, endSeekSilence } = useGlobalAudioPlayback();
  const barRef = useRef<HTMLDivElement | null>(null);
  const scrubPointerId = useRef<number | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const durationKnown = Number.isFinite(durationSec) && durationSec > 0;

  const getRatioFromClientX = useCallback((clientX: number) => {
    const el = barRef.current;
    if (!el) return 0;
    const { left, width } = el.getBoundingClientRect();
    if (width <= 0) return 0;
    return clamp01((clientX - left) / width);
  }, []);

  const endScrub = useCallback(
    (e: ReactPointerEvent) => {
      if (scrubPointerId.current !== e.pointerId) return;
      const t = barRef.current;
      if (t?.hasPointerCapture(e.pointerId)) t.releasePointerCapture(e.pointerId);
      scrubPointerId.current = null;
      setIsScrubbing(false);
      endSeekSilence();
    },
    [endSeekSilence],
  );

  const onTrackPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || !durationKnown) return;
      e.preventDefault();
      e.stopPropagation();
      scrubPointerId.current = e.pointerId;
      setIsScrubbing(true);
      beginSeekSilence();
      e.currentTarget.setPointerCapture(e.pointerId);
      seek(getRatioFromClientX(e.clientX));
    },
    [beginSeekSilence, durationKnown, getRatioFromClientX, seek],
  );

  const onTrackPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (scrubPointerId.current !== e.pointerId) return;
      seek(getRatioFromClientX(e.clientX));
    },
    [getRatioFromClientX, seek],
  );

  const onTrackPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (scrubPointerId.current !== e.pointerId) return;
      seek(getRatioFromClientX(e.clientX));
      endScrub(e);
    },
    [endScrub, getRatioFromClientX, seek],
  );

  const onTrackKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!durationKnown) return;
      const step = 5;
      const run = (ratio: number) => {
        beginSeekSilence();
        seek(ratio);
        endSeekSilence();
      };
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        run(clamp01((currentTimeSec - step) / durationSec));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        run(clamp01((currentTimeSec + step) / durationSec));
      } else if (e.key === 'Home') {
        e.preventDefault();
        run(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        run(1);
      }
    },
    [beginSeekSilence, currentTimeSec, durationKnown, durationSec, endSeekSilence, seek],
  );

  return {
    barRef,
    isScrubbing,
    durationKnown,
    currentTimeSec,
    durationSec: durationKnown ? durationSec : 0,
    progressRatio,
    ariaValueNow: Math.round(progressRatio * 1000),
    ariaValueMax: 1000,
    onTrackPointerDown,
    onTrackPointerMove,
    onTrackPointerUp,
    onTrackPointerCancel: endScrub,
    onTrackKeyDown,
  };
}
